import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, useTheme } from '@mui/material';
import useClinicDocument from 'hooks/useClinicDocument';
import useClinicDocuments from 'hooks/useClinicDocuments';
import useOptions from 'hooks/useOptions';
import { useSnackbar } from 'notistack';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import * as Yup from 'yup';
import DialogClose from '../buttons/DialogClose';
import CenteredLoading from '../CenteredLoading';
import CheckboxGroup from '../inputs/CheckboxGroup';
import FormContainer from '../inputs/FormContainer';
import TextField from '../inputs/TextField';

interface Props {
  editingFolderId?: string;
  onClose: () => void;
}

const AddDocuments = ({ editingFolderId, onClose }: Props) => {
  type FormValues = Yup.InferType<typeof validationSchema>;
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const { saveDocuments, isSavingDocument } = useClinicDocuments();

  const { document, updateDocument, isLoadingDocument } = useClinicDocument({ documentId: editingFolderId });
  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .trim()
      .min(1, intl.formatMessage({ defaultMessage: 'Title must contain at least one character' }))
      .required(intl.formatMessage({ defaultMessage: 'Title is required' })),
    targetAudience: Yup.array()
      .of(Yup.string())
      .required(intl.formatMessage({ defaultMessage: 'Target Audience is required' }))
  });
  const initialValues = useMemo(
    () => ({
      title: document?.title ?? '',
      targetAudience: document?.targetAudience ?? []
    }),
    [document]
  );

  const onFormSubmit = async (values: FormValues) => {
    const { title, targetAudience } = values;

    let data: any;

    if (!editingFolderId) {
      data = await saveDocuments({
        title,
        targetAudience
      });
    } else {
      data = await updateDocument({
        title,
        targetAudience
      });
    }

    enqueueSnackbar(data.message, { variant: 'success' });
    onClose();
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {!editingFolderId ? <FormattedMessage defaultMessage="New Folder" /> : <FormattedMessage defaultMessage="Edit Folder" />}
      </DialogTitle>
      <DialogClose onClose={onClose} />
      <Divider />
      <DialogContent>
        {isLoadingDocument ? (
          <CenteredLoading />
        ) : (
          <FormContainer validation={validationSchema} defaultValues={initialValues} onSuccess={onFormSubmit}>
            <DocumentsFormFields />

            <DialogActions sx={{ mt: 3 }}>
              <Divider />
              <Button variant="contained" type="submit" sx={{ width: 180 }} disabled={isSavingDocument}>
                {!editingFolderId ? <FormattedMessage defaultMessage="Create" /> : <FormattedMessage defaultMessage="Update" />}
              </Button>
            </DialogActions>
          </FormContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const DocumentsFormFields = () => {
  const theme = useTheme();
  const intl = useIntl();

  const { roles } = useOptions();

  return (
    <Stack spacing={4}>
      <Box>
        <TextField label={intl.formatMessage({ defaultMessage: 'Title' })} name="title" fullWidth />
      </Box>

      <Box
        sx={{ border: 1, borderColor: '#ddd', borderStyle: 'solid', borderRadius: theme.shape.borderRadius, p: 2, width: 'fit-content' }}
      >
        <CheckboxGroup
          name="targetAudience"
          label={intl.formatMessage({ defaultMessage: 'Target Audience' })}
          options={
            roles
              ?.filter((role: any) => !['NOUS_SUPPORT', 'CLIENT'].includes(role.roleName))
              ?.map((role: any) => ({
                label: intl.locale === 'fr' ? role?.roleNameFrench : role.roleName,
                id: role.roleName
              })) ?? []
          }
          row
        />
      </Box>
    </Stack>
  );
};

export default AddDocuments;
